import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useOverlayLayer } from './overlayStack';
import { useDialogFocus } from './useDialogFocus';

export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';
export type DrawerSide = 'right' | 'left';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: DrawerSize;
  side?: DrawerSide;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

const sizeStyles: Record<DrawerSize, string> = {
  sm: 'w-[360px]',
  md: 'w-[520px]',
  lg: 'w-[720px]',
  xl: 'w-[960px]',
};

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  side = 'right',
  children,
  footer,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  className,
}) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  // Блокировкой прокрутки теперь заведует стек: иначе закрытие окна поверх
  // панели разблокировало бы страницу под ещё открытой панелью.
  const { isTopmost } = useOverlayLayer(isOpen);
  useDialogFocus(isOpen, dialogRef, isTopmost);

  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Клавишей владеет верхний оверлей. Не верхний — молчит и событие не
      // гасит: гасить должен тот, кто его обработал.
      if (!isTopmost()) return;
      // Esc уже обработал элемент внутри окна (открытый список Select/Combobox закрылся и погасил событие
      // через preventDefault) — окно не закрываем: одно нажатие закрывает один слой.
      if (event.defaultPrevented) return;
      if (closeOnEsc) onClose();
    },
    [closeOnEsc, onClose, isTopmost]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, handleEsc]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 !m-0 bg-black/40 animate-fadeIn"
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        className={cn(
          'fixed inset-y-0 z-50 !m-0 flex flex-col bg-white shadow-medium max-w-[90vw] dark:bg-zinc-900',
          side === 'right'
            ? 'right-0 animate-slide-in-right'
            : 'left-0 animate-slide-in-left',
          sizeStyles[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        data-pim-dialog=""
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Подробности'}
        aria-describedby={subtitle ? descriptionId : undefined}
      >
        {(title || subtitle || showCloseButton) && (
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 shrink-0 dark:border-zinc-800">
            <div className="min-w-0 flex-1">
              {title && (
                <h2 id={titleId} className="text-lg font-semibold text-gray-900 leading-tight truncate dark:text-zinc-100">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p id={descriptionId} className="mt-0.5 text-xs text-gray-500 truncate dark:text-zinc-400">{subtitle}</p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 -mt-1 -mr-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors p-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                aria-label="Закрыть"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-5 dark:bg-zinc-900">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0 dark:border-zinc-800 dark:bg-zinc-900/90">
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

/**
 * KvList — key-value definition list, classic right-drawer detail pattern.
 * Usage: <Kv label="SKU">{sku}</Kv>
 */
export const KvList: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <dl className={cn('grid grid-cols-[120px_1fr] gap-x-4 gap-y-3 text-sm', className)}>
    {children}
  </dl>
);

export const Kv: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <dt className="text-gray-500 dark:text-zinc-400">{label}</dt>
    <dd className="text-gray-900 font-medium m-0 dark:text-zinc-100">{children}</dd>
  </>
);

export default Drawer;
